import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, Briefcase, Package, UserCog, LogOut, Menu, X, Settings, Warehouse, BarChart3, ShieldCheck, Calendar, Users2, Route, Sparkles, Cloud, MessageCircle, AlertOctagon, Send, Car, LineChart, CalendarClock, Wrench, Sun, MessagesSquare, CalendarDays,
} from "lucide-react";
import { Toaster } from "sonner";
import IdleGuard from "./IdleGuard";
import BrandLogo from "./BrandLogo";
import ActiveJobsBadge from "./ActiveJobsBadge";
import FollowupBell from "./FollowupBell";
import VeicoliBell from "./VeicoliBell";
import NotificheBell from "./NotificheBell";
import ChannelsDock from "./ChannelsDock";
import { AppInstallButton } from "./AppInstallButton";
import { useResponsiveTables } from "../lib/responsiveTables";
import { ChannelsProvider } from "../context/ChannelsContext";
import { canSeeMenu } from "../lib/menu";

const NAV = [
  { key: "dashboard", to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { key: "mia_agenda", to: "/mia-agenda", icon: CalendarDays, label: "La mia agenda", testid: "nav-mia-agenda" },
  { key: "clienti", to: "/clienti", icon: Users, label: "Clienti", testid: "nav-clienti" },
  { key: "commesse", to: "/commesse", icon: Briefcase, label: "Commesse", testid: "nav-commesse" },
  { key: "prodotti", to: "/prodotti", icon: Package, label: "Prodotti", testid: "nav-prodotti" },
  { key: "magazzino", to: "/magazzino", icon: Warehouse, label: "Magazzino", testid: "nav-magazzino" },
  { key: "calendario", to: "/calendario", icon: Calendar, label: "Calendario", testid: "nav-calendario" },
  { key: "chat", to: "/chat", icon: MessagesSquare, label: "Comunicazioni", testid: "nav-chat" },
  { key: "installazioni_da_pianificare", to: "/installazioni-da-pianificare", icon: CalendarClock, label: "Installazioni da Pianificare", testid: "nav-da-pianificare", roles: ["admin", "operatore"] },
  { key: "interventi_da_pianificare", to: "/interventi-da-pianificare", icon: Wrench, label: "Interventi da Pianificare", testid: "nav-interventi-da-pianificare", roles: ["admin", "operatore", "tecnico"] },
  { key: "progetti_3d", to: "/progetti-3d", icon: Sun, label: "Progetti FV", testid: "nav-progetti-3d", roles: ["admin", "operatore", "tecnico"] },
  { key: "miei_canali", to: "/miei-canali", icon: Send, label: "I miei canali", testid: "nav-miei-canali" },
  { key: "itinerario", to: "/itinerario", icon: Sparkles, label: "Itinerario", testid: "nav-itinerario" },
  { key: "pianificazione_interventi", to: "/pianificazione-interventi", icon: Route, label: "Pianif. interventi", testid: "nav-pian-interventi" },
  { key: "veicoli", to: "/veicoli", icon: Car, label: "Veicoli Aziendali", testid: "nav-veicoli" },
  { key: "reports", to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports" },
  { key: "analytics", to: "/analytics", icon: LineChart, label: "Analytics", testid: "nav-analytics", adminOnly: true },
  { key: "squadre", to: "/squadre", icon: Users2, label: "Squadre", testid: "nav-squadre", adminOnly: true },
  { key: "utenti", to: "/utenti", icon: UserCog, label: "Utenti", testid: "nav-utenti", adminOnly: true },
  { key: "audit_logs", to: "/audit-logs", icon: ShieldCheck, label: "Log attività", testid: "nav-audit", adminOnly: true },
  { key: "aruba_jobs", to: "/aruba-jobs", icon: Cloud, label: "Aruba Drive · Job", testid: "nav-aruba-jobs", adminOnly: true },
  { key: "whatsapp_settings", to: "/whatsapp-settings", icon: MessageCircle, label: "WhatsApp", testid: "nav-whatsapp", adminOnly: true },
  { key: "admin_errors", to: "/admin/errors", icon: AlertOctagon, label: "Errori 5xx", testid: "nav-admin-errors", adminOnly: true },
  { key: "impostazioni", to: "/impostazioni", icon: Settings, label: "Impostazioni", testid: "nav-impostazioni", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const filteredNav = NAV.filter((n) => (!n.adminOnly || user?.role === "admin") && canSeeMenu(user, n.key));
  const [chatUnread, setChatUnread] = useState(0);
  React.useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { api } = await import("../lib/api");
        const { data } = await api.get("/chats/unread-total");
        if (alive) setChatUnread(data.total || 0);
      } catch { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useResponsiveTables();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      <Toaster position="top-right" richColors />
      <IdleGuard />
      <ChannelsProvider>

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`${open ? "fixed inset-y-0 left-0 z-50 flex" : "hidden"} lg:flex lg:sticky lg:top-0 lg:flex-shrink-0 flex-col w-64 h-[100dvh] border-r`}
        style={{ background: "var(--sidebar)", borderColor: "var(--border)" }}
      >
        <div className="h-16 flex-shrink-0 flex items-center gap-2 px-5 border-b" style={{ borderColor: "var(--border)" }}>
          <BrandLogo size={36} />
          <div>
            <div className="font-extrabold text-[15px]" style={{ fontFamily: "Manrope" }}>
              Nureal<span style={{ color: "#FB8C00" }}>·</span>Operation
            </div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>CRM Fotovoltaico</div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="ml-auto lg:hidden p-1.5 rounded hover:bg-black/5"
            data-testid="sidebar-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4 space-y-1" style={{ WebkitOverflowScrolling: "touch" }} data-testid="sidebar-nav">
          {filteredNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              onClick={() => setOpen(false)}
              data-testid={n.testid}
            >
              <n.icon className="w-4 h-4" />
              {n.label}
              {n.key === "chat" && chatUnread > 0 && (
                <span className="ml-auto text-[11px] font-bold text-white rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center" style={{ background: "#1B5E20" }} data-testid="nav-chat-unread">{chatUnread}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex-shrink-0 p-3 border-t" style={{ borderColor: "var(--border)", paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="sidebar-user">
          <div className="card-shell p-3 flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
              style={{ background: "var(--primary)" }}
            >
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs capitalize" style={{ color: "var(--text-muted)" }}>{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded hover:bg-black/5"
              title="Logout"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="h-16 border-b px-4 lg:px-6 flex items-center justify-between sticky top-0 z-30"
          style={{ background: "var(--surface)", borderColor: "var(--border)" }}
        >
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 rounded hover:bg-black/5"
            data-testid="sidebar-open"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <AppInstallButton />
          <NotificheBell />
          <FollowupBell />
          <VeicoliBell />
          <ActiveJobsBadge />
          <div className="text-xs ml-4" style={{ color: "var(--text-muted)" }}>
            {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </header>

        <main className="flex-1 min-w-0 p-4 pb-24 lg:p-8">
          <Outlet />
        </main>
      </div>
      {!useLocation().pathname.startsWith("/chat") && <ChannelsDock />}
      </ChannelsProvider>
    </div>
  );
}
